import { CollapsibleSection } from './CollapsibleSection';
import {
  MAX_QR_SIZE_MM,
  MIN_QR_SIZE_MM,
  clampQrSize,
  type CardPrintJobConfig,
  type CropMarks,
  type FillBehavior,
} from './jobConfig';

/**
 * AW6 — 印刷ジョブ設定パネル (adminNote / 行間 / QR / 開始セル / カットライン / 埋め方)。
 * old2 `CardLayoutSettingsPanel.tsx` L90-144 を v2 に移植。
 *
 * 表示項目 (fields) の add/remove/reorder は `FieldsEditor` が担当 (本パネルとは別 section)。
 *
 * 2026-06-13: 操作盤が縦に長すぎる問題への対処で、各セクションを `CollapsibleSection` で
 * 折りたたみ可能化し、表示順を「管理者の追記欄 → 項目の行間 → QRコードの大きさ → 開始セル位置 →
 * カットライン → 投稿数<10件」に並べ替えた (FieldsEditor の 表示項目/追加できる項目 の直後に続く)。
 * よく使う「管理者の追記欄」のみ既定で開き、他は初期折りたたみで操作盤を短く保つ。
 */

export interface JobSettingsPanelProps {
  config: CardPrintJobConfig;
  onChange: (next: CardPrintJobConfig) => void;
  disabled?: boolean;
  /**
   * 出力モード。'pptx' (PowerPoint) のときは A4 面付け固有の設定
   * (項目の行間 / QRサイズ / 開始セル / カットライン / 投稿数<10件) を隠す
   * (1 投稿 1 スライドには存在しない概念のため)。
   */
  outputMode?: 'pdf' | 'pptx';
}

export function JobSettingsPanel({
  config,
  onChange,
  disabled,
  outputMode = 'pdf',
}: JobSettingsPanelProps): JSX.Element {
  const isPptx = outputMode === 'pptx';
  return (
    <div className="grid grid-cols-1 gap-2 text-sm" data-testid="card-job-settings">
      <CollapsibleSection title="管理者の追記欄" defaultOpen>
        <input
          type="text"
          value={config.adminNote}
          onChange={(e) => onChange({ ...config, adminNote: e.target.value })}
          disabled={disabled}
          placeholder="例: ○○防災訓練 2026/05/01"
          aria-label="管理者追記テキスト"
          className="w-full px-2 py-1 text-sm border border-slate-300 rounded disabled:bg-slate-50"
        />
        <p className="text-[10px] text-slate-500 mt-1">
          表示項目に「管理者追記」を含めると、全カード共通でこのテキストが印字されます。
        </p>
      </CollapsibleSection>

      {!isPptx && (
        <>
          <CollapsibleSection title="項目の行間" defaultOpen={false}>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={0}
                max={4}
                step={0.1}
                value={config.fieldGap_mm}
                onChange={(e) => onChange({ ...config, fieldGap_mm: Number(e.target.value) })}
                disabled={disabled}
                aria-label="項目の行間 (mm)"
                className="flex-1"
              />
              <span className="text-xs tabular-nums w-12 text-right">
                {config.fieldGap_mm.toFixed(1)}mm
              </span>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              項目どうしの間隔です。小さくすると詰まり、より多くの項目を 1 枚に載せられます (既定
              0.6mm)。
            </p>
          </CollapsibleSection>

          <CollapsibleSection title="QRコードの大きさ" defaultOpen={false}>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={MIN_QR_SIZE_MM}
                max={MAX_QR_SIZE_MM}
                step={1}
                value={config.qrSize_mm}
                onChange={(e) =>
                  onChange({ ...config, qrSize_mm: clampQrSize(Number(e.target.value)) })
                }
                disabled={disabled}
                aria-label="QRコードの大きさ (mm)"
                className="flex-1"
              />
              <span className="text-xs tabular-nums w-12 text-right">{config.qrSize_mm}mm</span>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              表示項目に「QRコード(地図リンク)」を含めたときに使われます。大きくすると、にじみやすい
              インクジェット印刷でも読み取りやすくなります (既定 8mm / カード右下に配置)。
            </p>
          </CollapsibleSection>

          <CollapsibleSection title="開始セル位置" defaultOpen={false}>
            <div className="grid grid-cols-2 gap-1 w-[120px]" data-testid="card-start-cell-grid">
              {Array.from({ length: 10 }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onChange({ ...config, startIndex: i })}
                  disabled={disabled}
                  aria-label={`開始セル ${i + 1}`}
                  aria-pressed={config.startIndex === i}
                  className={`p-2 text-xs border rounded disabled:opacity-40 ${
                    config.startIndex === i
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white border-slate-300 hover:border-primary-400'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              残ったラベル台紙から印刷したいときに使います。1ページ目の先頭セルは緑枠で表示されます。
            </p>
          </CollapsibleSection>

          <CollapsibleSection title="カットライン" defaultOpen={false}>
            <select
              value={config.cropMarks}
              onChange={(e) => onChange({ ...config, cropMarks: e.target.value as CropMarks })}
              disabled={disabled}
              aria-label="カットライン"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded bg-white"
            >
              <option value="none">なし (本番ラベル印刷推奨)</option>
              <option value="corners">L字マーク (コーナー)</option>
              <option value="lines">破線 (全幅)</option>
            </select>
          </CollapsibleSection>

          <CollapsibleSection title="投稿数 < 10件のとき" defaultOpen={false}>
            <select
              value={config.fillBehavior}
              onChange={(e) =>
                onChange({ ...config, fillBehavior: e.target.value as FillBehavior })
              }
              disabled={disabled}
              aria-label="投稿数が10件未満のときの埋め方"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded bg-white"
            >
              <option value="leave-empty">空セルのまま</option>
              <option value="repeat-posts">同じ投稿を繰り返してページを埋める</option>
            </select>
          </CollapsibleSection>
        </>
      )}
    </div>
  );
}
